import { Dispatch, FC, SetStateAction, UIEventHandler, useCallback, useContext, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";
import { ISortOption } from "../../../pages/UserMonitor/UserMonitor";
import SortDirectionArrow from "../../../assets/sortDirectionArrow.svg";
import TaskColumn from "../../Shared/TaskColumn";
import "../../../pages/UserMonitor/UserMonitor.css";
import "./TasksTable.css";
import { AppContext } from "../../../App";
import TableRow from "./TableRow";
import { IMonitorTaskDisplay } from "../../../common/api_interfaces/monitor/IUserMonitorTaskFullInfo";

interface ITasksTableProps {
    dataSort: ISortOption[];
    onSortChange: (code: string) => void;
    addSort: (code: string) => void;
    tasks: IMonitorTaskDisplay[];
    selectedTaskId: number | undefined;
    setSelectedTaskId: Dispatch<SetStateAction<number | undefined>>;
    favouriteTaskIds: number[];
    updateFavourite: (newFavourite: { favourited: number[] }) => void;
    onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
}

const TasksTable: FC<ITasksTableProps> = observer(({
    dataSort,
    onSortChange,
    addSort,
    tasks,
    selectedTaskId,
    setSelectedTaskId,
    favouriteTaskIds,
    updateFavourite,
    onScroll,
}) => {
    const { showToast } = useContext(AppContext);

    const handleFavouriteClick = useCallback((taskId: number) => {
        const isFavourite = favouriteTaskIds.includes(taskId);
        updateFavourite({
            favourited: isFavourite
                ? favouriteTaskIds.filter(id => id !== taskId)
                : [...favouriteTaskIds, taskId]
        });
    }, [favouriteTaskIds, updateFavourite]);

    const handleRowClick = useCallback((taskId: number) => {
        setSelectedTaskId(prev => prev === taskId ? undefined : taskId);
    }, [setSelectedTaskId]);

    const handleHeaderClick = useCallback((item: ISortOption) => {
        if (item.code !== "columnTask") {
            item.sort ? onSortChange(item.code) : addSort(item.code);
        }
    }, [onSortChange, addSort]);

    const taskColumnDisplay = useCallback((task: IMonitorTaskDisplay) => (
        <div className="column-display-container">
            <div>
                {task.columns.main && (
                    <TaskColumn name={task.columns.main.name} color={task.columns.main.color} />
                )}
            </div>
            {task.columns.proxy && (
                <TaskColumn name={task.columns.proxy.name} color={task.columns.proxy.color} />
            )}
        </div>
    ), []);

    const renderTaskLink = useCallback((task: IMonitorTaskDisplay, content: React.ReactNode, className: string) => {
        if (task.isGranted) {
            return (
                <Link
                    className={className}
                    to={`/task/${task.id}`}
                    style={{ color: "inherit", textDecoration: "none" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {content}
                </Link>
            );
        }

        return (
            <div
                className={className}
                onClick={(e) => {
                    e.stopPropagation();
                    showToast("У вас нет прав на задачу");
                }}
            >
                {content}
            </div>
        );
    }, [showToast]);

    const memoizedTasks = useMemo(() => tasks, [tasks]);

    return (
        <div className="user-monitor-widget-rows-display-part card-shadow">
            <div 
                onScroll={onScroll} 
                className="user-monitor-widget-rows-display-part-table-container"
            >
                <table className="monitor-table-guards">
                    <thead className="custom_table_thead">
                        <tr>
                            <th style={{ width: "3%" }}></th>
                            {dataSort.map((item) => (
                                <th
                                    key={item.code}
                                    onClick={() => handleHeaderClick(item)}
                                    className={`
                                        user-monitor-toolbar-left-part-text 
                                        ${item.sort ? "no-highlight" : ""} 
                                        sort-direction-changing-header 
                                        ${item.code} 
                                        ${item.sort === "asc" ? "" : "desc"}
                                    `}
                                    style={{ width: "100px" }}
                                >
                                    <div>
                                        {item.lable}
                                        {item.sort && <img src={SortDirectionArrow} alt="" />}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {memoizedTasks.map((task) => (
                            <TableRow
                                key={task.id}
                                task={task}
                                isSelected={selectedTaskId === task.id}
                                isFavourite={favouriteTaskIds.includes(task.id)}
                                onRowClick={handleRowClick}
                                onFavouriteClick={handleFavouriteClick}
                                taskColumnDisplay={taskColumnDisplay}
                                renderTaskLink={renderTaskLink}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
});

export default TasksTable;